import type { V1Thread } from '@/generated/hooks/rain/models';

/**
 * The threads carrying every scope variable, newest activity first.
 *
 * @param threads The agent's threads as listed.
 * @param variables The variables a thread must carry, with their values.
 * @returns The matching threads that have an id.
 */
export function scopedThreads(
  threads: readonly V1Thread[] | undefined,
  variables: Record<string, string>,
): V1Thread[] {
  const required = Object.entries(variables);
  return (threads ?? [])
    .filter(
      thread =>
        Boolean(thread.thread_id) &&
        required.every(([key, value]) => thread.variables?.[key] === value),
    )
    .sort((a, b) =>
      (b.updated_at ?? b.created_at ?? '').localeCompare(
        a.updated_at ?? a.created_at ?? '',
      ),
    );
}

/**
 * The variables a new thread is created with, so the scope's list finds it again.
 *
 * @param organization Organization the thread belongs to.
 * @param workspace Workspace the thread belongs to.
 * @param variables The scope variables to seed.
 * @returns The thread's variables.
 */
export function seededThreadVariables(
  organization: string,
  workspace: string,
  variables: Record<string, string>,
): Record<string, string> {
  return { organizationId: organization, workspaceId: workspace, ...variables };
}

/**
 * A list filter admitting only threads that carry every scope variable.
 *
 * @param variables The variables a thread must carry, with their values.
 * @returns The filter expression; empty when there is nothing to narrow by.
 */
export function scopeFilter(variables: Record<string, string>): string {
  return Object.entries(variables)
    .map(([key, value]) => `variables.${key} == ${JSON.stringify(value)}`)
    .join(' && ');
}
